import { lazy, Suspense, type ReactNode } from 'react';
import { Label } from '../label';
import { cn } from '../../lib/utils';
import { useFormOptionalLabel } from './form-optional-label';
import { useFormReadOnly } from './form-read-only';
import { useFieldFallback } from './field-fallback';

const FieldInfoTooltip = lazy(() => import('./field-info-tooltip'));

// `<label htmlFor>` only auto-focuses native form controls. For contenteditable
// editors (e.g. tiptap's ProseMirror element), the click does nothing — so we
// manually focus the target when the label is clicked.
function focusContentEditableOnClick(e: React.MouseEvent<HTMLLabelElement>, htmlFor: string) {
	const el = document.getElementById(htmlFor);
	if (el?.isContentEditable) {
		e.preventDefault();
		el.focus();
	}
}

export function FieldLabelRow({
	label,
	htmlFor,
	hasError,
	optional,
	info,
	badge,
	className,
}: {
	label: string;
	htmlFor: string;
	hasError: boolean;
	optional?: boolean;
	info?: ReactNode;
	/** Optional adornment rendered next to the label (e.g. an "inherited from project" badge). */
	badge?: ReactNode;
	className?: string;
}) {
	const optionalLabel = useFormOptionalLabel();
	const formReadOnly = useFormReadOnly();
	// A wrapper around this field may have something to say about where its value comes from. It sits
	// on the label row because that row is already there: the annotation costs the form no height.
	const fallback = useFieldFallback();

	return (
		<div className={cn('flex items-center justify-between gap-3', className)}>
			<div className="flex min-h-5 items-center gap-1">
				<Label
					data-slot="form-label"
					data-error={hasError}
					className="data-[error=true]:text-destructive"
					htmlFor={htmlFor}
					onClick={(e) => focusContentEditableOnClick(e, htmlFor)}
				>
					{label}
				</Label>
				{info && (
					<Suspense>
						<FieldInfoTooltip info={info} />
					</Suspense>
				)}
				{badge}
				{fallback?.annotation}
			</div>
			{optional && (
				<span className={cn('text-xs text-muted-foreground transition-opacity', formReadOnly && 'opacity-0')}>
					{optionalLabel}
				</span>
			)}
		</div>
	);
}

export function FieldDescription({ children }: { children: ReactNode }) {
	return <p className="text-muted-foreground text-xs">{children}</p>;
}
